import { IActiveDestination } from '../../models/Destination';
import { IContact } from '../../models/Company';
import { ITaxCode } from '../../models/TaxCode';

/** Fixed render order of the palette's sections — instant client-side sections first. */
export type SectionKey =
    | 'productSearch'
    | 'actions'
    | 'frequent'
    | 'settings'
    | 'storefronts'
    | 'workspaces'
    | 'contacts'
    | 'companies'
    | 'users'
    | 'extensions'
    | 'vendors'
    | 'taxcodes'
    | 'categories'
    | 'segments'
    | 'orders'
    | 'carts'
    | 'bundles'
    | 'savedSearches'
    | 'products';

/** Lower sorts first. Same scale as the settings search box, shared by every client-ranked section. */
export const RANK_LABEL_PREFIX = 0;
export const RANK_LABEL_SUBSTRING = 1;
export const RANK_ID_SUBSTRING = 2;
export const RANK_DESCRIPTION_SUBSTRING = 3;

/**
 * Where a term matches, ranked label-prefix best to description-substring worst.
 * Returns undefined for no match. `term` must already be lowercased/trimmed.
 */
export const matchRank = (term: string, label: string, id?: string, description?: string): number | undefined => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.startsWith(term)) return RANK_LABEL_PREFIX;
    if (lowerLabel.includes(term)) return RANK_LABEL_SUBSTRING;
    if (id && id.toLowerCase().includes(term)) return RANK_ID_SUBSTRING;
    if (description && description.toLowerCase().includes(term)) return RANK_DESCRIPTION_SUBSTRING;
    return undefined;
};

/** Rank, then label, capped. For client-filtered sections; server sections keep API order. */
export const sortHits = (hits: IGlobalSearchHit[], cap: number): IGlobalSearchHit[] =>
    hits
        .sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            return a.label.localeCompare(b.label);
        })
        .slice(0, cap);

/**
 * A modal/panel the palette can request from `CreateEntityHost` — either a create flow launched by
 * a shortcut action, or a details view for a picked row that has no route of its own.
 */
export type EntityRequest =
    | { kind: 'createWorkspace' }
    | { kind: 'createContact' }
    | { kind: 'contact', contact: IContact }
    | { kind: 'createCompany' }
    | { kind: 'company', companyId: string }
    | { kind: 'createStorefront' }
    | { kind: 'createApiKey' }
    | { kind: 'createWebhook' }
    | { kind: 'installExtension' }
    | { kind: 'createTaxCode' }
    | { kind: 'taxCode', taxCode: ITaxCode }
    | { kind: 'productCategory', categoryIdentifier: string, segmentIdentifier: string }
    | { kind: 'productSegment', segmentIdentifier: string }
    | { kind: 'product', productId: string, /** Where the search was opened from: the details modal's Add defaults to it. */ destination?: IActiveDestination, /** Tab to open on (a thumbnail click asks for 'media'). */ initialTab?: 'details' | 'media' | 'vendor' | 'purchaseHistory' }
    | { kind: 'order', orderId: string }
    | { kind: 'cart', cartId: string }
    // A prepared cart's id is its workspace id — PreparedCartPanel keys off the workspace.
    | { kind: 'preparedCart', workspaceId: string }
    // Pick a PSA opportunity, then run the quote-link quick flow for the given import extension.
    | { kind: 'preparedCartFromOpportunity', extensionId: string };

export interface IGlobalSearchHit {
    section: SectionKey;
    /** Unique within the section — used for React keys. */
    key: string;
    label: string;
    /** Subtitle: email, storefront name, part number, breadcrumb, … */
    secondary?: string;
    /** Fluent icon name for the row. */
    icon?: string;
    rank: number;
    /** Executes the hit: navigate, dispatch, or request a modal. The palette closes itself after. */
    run: () => void;
    /** An in-place action rendered as a trailing button (and bound to Shift+Enter) that keeps the
     *  palette open — e.g. "Add" to the workspace the palette was opened from. Resolves true on success. */
    secondaryRun?: () => Promise<boolean>;
    /** Button text for secondaryRun ("Add"). */
    secondaryLabel?: string;
    /** Button text once secondaryRun has succeeded ("Added"). */
    secondaryDoneLabel?: string;
    /** Run without closing the palette (e.g. a product details modal opened over a workspace-context
     *  search): dismissing what opened lands the user back where they were. */
    keepOpen?: boolean;
    /** Shown in a flyout after hovering the row for a moment: the item's most important details. */
    preview?: IHitPreview;
}

export interface IHitPreview {
    title: string;
    image?: string;
    /** Short facts, one per line (manufacturer · part number, cost, stock, why suggested, …). */
    lines: string[];
    /** Longer text under the facts, already trimmed to a sentence or two. */
    description?: string;
}

/** Where the palette was opened from, when that should shape what it offers. */
export type IGlobalSearchContext =
    | { kind: 'workspace'; workspaceId: string; /** Resolved by the palette for chips and labels. */ name?: string }
    | { kind: 'bundle'; bundleId: string; name?: string };

/** The add-to destination a context stands for (product rows' in-place Add, the details modal's split button). */
export const contextDestination = (context?: IGlobalSearchContext): IActiveDestination | undefined => {
    if (!context) return undefined;
    if (context.kind === 'workspace') return { type: 'workspace', id: context.workspaceId, name: context.name };
    return { type: 'bundle', id: context.bundleId, name: context.name };
};

export interface ISectionResult {
    section: SectionKey;
    /** Section heading shown in the palette. */
    label: string;
    /** True while this section's server query is in flight — sections load independently. */
    isLoading: boolean;
    hits: IGlobalSearchHit[];
}

/** Everything a section hook needs from the palette. */
export interface ISectionArgs {
    /** Raw input, lowercased+trimmed — drives the instant client-side sections. */
    term: string;
    /** Trimmed input with original casing — the product passthrough parser needs it because
     *  downstream keyword-field filters (category, manufacturer, attributes) are case-sensitive. */
    rawTerm: string;
    /** Debounced input, lowercased+trimmed — drives the server-hit sections. */
    debouncedTerm: string;
    /** Debounced input with original casing — must match the palette's facet-preview arg exactly
     *  so the passthrough dispatcher shares its RTK cache entry. */
    debouncedRawTerm: string;
    /** Only query while the palette is actually open. */
    isOpen: boolean;
    /** Ask CreateEntityHost to show a modal (create flow or routeless details view). */
    openEntity: (request: EntityRequest) => void;
    /** The page the palette was opened from, e.g. a workspace whose rows should offer "Add". */
    context?: IGlobalSearchContext;
}

/** Server-hit sections wait for 2+ characters so a single keystroke doesn't fan out queries. */
export const MIN_SERVER_TERM = 2;

/**
 * Phrases that name a section inside a query. "UPS saved search" hints the Saved Searches
 * section: it matches on the stripped term ("ups") and renders first. Longest alias wins.
 */
const SECTION_HINT_ALIASES: [SectionKey, string][] = [
    ['savedSearches', 'saved searches'],
    ['savedSearches', 'saved search'],
    ['taxcodes', 'tax codes'],
    ['taxcodes', 'tax code'],
    ['carts', 'prepared carts'],
    ['carts', 'prepared cart'],
    ['settings', 'settings'],
    ['settings', 'setting'],
    ['storefronts', 'storefronts'],
    ['storefronts', 'storefront'],
    ['workspaces', 'workspaces'],
    ['workspaces', 'workspace'],
    ['contacts', 'contacts'],
    ['contacts', 'contact'],
    ['companies', 'companies'],
    ['companies', 'company'],
    ['users', 'users'],
    ['users', 'user'],
    ['extensions', 'extensions'],
    ['extensions', 'extension'],
    ['extensions', 'folio'],
    ['vendors', 'vendors'],
    ['vendors', 'vendor'],
    ['categories', 'categories'],
    ['categories', 'category'],
    ['segments', 'segments'],
    ['segments', 'segment'],
    ['orders', 'orders'],
    ['orders', 'order'],
    ['carts', 'carts'],
    ['carts', 'cart'],
    ['carts', 'quotes'],
    ['carts', 'quote'],
    ['bundles', 'bundles'],
    ['bundles', 'bundle'],
    ['products', 'products'],
    ['products', 'product'],
];

export interface ISectionHint {
    section?: SectionKey;
    /** The term with the hint phrase removed; equals the input when no hint matched. */
    stripped: string;
}

/**
 * Detects a leading or trailing section-name phrase in a (lowercased) term. Only fires when
 * something remains after stripping — "orders" alone is a search, not a hint.
 */
export const parseSectionHint = (term: string): ISectionHint => {
    for (const [section, alias] of SECTION_HINT_ALIASES) {
        let stripped: string | undefined;
        if (term.endsWith(` ${alias}`)) stripped = term.slice(0, -alias.length - 1).trim();
        else if (term.startsWith(`${alias} `)) stripped = term.slice(alias.length + 1).trim();
        if (stripped) return { section, stripped };
    }
    return { stripped: term };
};

export interface INameScopeMatch {
    id: string;
    label: string;
    /** Token range (start inclusive, end exclusive) the name consumed. */
    tokenStart: number;
    tokenEnd: number;
    rank: number;
}

/**
 * Finds a contiguous token window that names one of the options — "warp speed orders" resolves
 * "warp speed" to the storefront Warp Speed Solutions, leaving "orders" for screen matching.
 * Widest window wins, then label-prefix over substring, then alphabetical for determinism.
 * A single-source version of the settings search box's findInstanceScope.
 */
export const findNameScope = (tokens: string[], options: { id: string, label: string }[]): INameScopeMatch | undefined => {
    let best: INameScopeMatch | undefined;
    for (let start = 0; start < tokens.length; start++) {
        for (let end = tokens.length; end > start; end--) {
            const phrase = tokens.slice(start, end).join(' ');
            for (const option of options) {
                const lowerName = option.label.toLowerCase();
                let rank: number | undefined;
                if (lowerName.startsWith(phrase)) rank = RANK_LABEL_PREFIX;
                else if (lowerName.includes(phrase)) rank = RANK_LABEL_SUBSTRING;
                if (rank === undefined) continue;

                const width = end - start;
                const bestWidth = best ? best.tokenEnd - best.tokenStart : -1;
                const better =
                    !best ||
                    width > bestWidth ||
                    (width === bestWidth && rank < best.rank) ||
                    (width === bestWidth && rank === best.rank && option.label.localeCompare(best.label) < 0);
                if (better) {
                    best = { id: option.id, label: option.label, tokenStart: start, tokenEnd: end, rank };
                }
            }
        }
    }
    return best;
};
